// Invoices & Billing Component
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import FeatureGuard from '../common/FeatureGuard';
import {
  Box,
  Typography,
  Button,
  TextField,
  IconButton,
  InputAdornment,
  Alert,
  Menu,
  MenuItem,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  Divider,
  Chip,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import {
  Add,
  Search,
  Edit,
  Delete,
  Receipt,
  Refresh,
  Print,
  Send,
  Payment,
  History,
  Visibility,
  AccountBalance,
} from '@mui/icons-material';
import { useCurrency } from '../../contexts/CurrencyContext';
import apiService from '../../services/api';
import authService from '../../services/auth';
import InvoicePrint from './InvoicePrint';
import { printInvoice } from '../../utils/invoiceUtils';
import { DataTable } from '../common';

const Invoices = () => {
  const navigate = useNavigate();
  const [invoices, setInvoices] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [dateFilter, setDateFilter] = useState('current_month');
  const [customDateRange, setCustomDateRange] = useState({
    startDate: '',
    endDate: ''
  });
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [paymentHistoryDialog, setPaymentHistoryDialog] = useState(false);
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [loadingPayments, setLoadingPayments] = useState(false);
  const [receiveBalanceDialog, setReceiveBalanceDialog] = useState(false);
  const [receiveBalanceData, setReceiveBalanceData] = useState({
    payment_mode: 'cash',
    payment_date: new Date().toISOString().split('T')[0],
    reference_number: '',
    notes: '',
    amount: 0
  });
  const [savingPayment, setSavingPayment] = useState(false);
  const [deleteConfirmDialog, setDeleteConfirmDialog] = useState(false);
  const [userPermissions, setUserPermissions] = useState(null);
  
  // Use currency context for proper currency formatting
  const { formatPrice } = useCurrency();

  const isAdminLike = (user) => String(user?.role_name || '').toLowerCase().includes('admin');

  const hasPermission = (module, action = 'view') => {
    const user = authService.getUser();
    if (isAdminLike(user)) return true;
    if (!userPermissions) return false; // null/undefined => treat as denied until loaded
    return userPermissions?.[module]?.[action] === true;
  };

  const fetchPermissions = async () => {
    try {
      const user = authService.getUser();
      if (!user?.id) return;
      const response = await apiService.get(`/roles/user/${user.id}/permissions`);
      setUserPermissions(response.data || {});
    } catch (e) {
      // safest fallback: no permissions
      setUserPermissions({});
    }
  };

  const statusOptions = [
    { value: 'draft', label: 'Draft', color: 'default' },
    { value: 'submitted', label: 'Submitted', color: 'primary' },
  ];

  // DataTable columns configuration
  const columns = [
    {
      field: 'invoice_number',
      header: 'Invoice #',
      type: 'text',
      sortable: true,
      exportable: true,
      exportWidth: 15,
    },
    {
      field: 'client',
      header: 'Client',
      type: 'client',
      sortable: true,
      exportable: true,
      exportWidth: 25,
    },
    {
      field: 'invoice_date',
      header: 'Date',
      type: 'date',
      sortable: true,
      exportable: true,
      exportWidth: 12,
    },
    {
      field: 'status',
      header: 'Status',
      type: 'status',
      sortable: true,
      exportable: true,
      exportWidth: 12,
    },
    {
      field: 'subtotal',
      header: 'Amount',
      type: 'amount',
      sortable: true,
      exportable: true,
      align: 'right',
      exportWidth: 15,
    },
    {
      field: 'discount_amount',
      header: 'Discount',
      type: 'amount',
      sortable: true,
      exportable: true,
      align: 'right',
      exportWidth: 15,
    },
    {
      field: 'gst_amount',
      header: 'Tax',
      type: 'amount',
      sortable: true,
      exportable: true,
      align: 'right',
      exportWidth: 15,
    },
    {
      field: 'total_amount',
      header: 'Final',
      type: 'amount',
      sortable: true,
      exportable: true,
      align: 'right',
      exportWidth: 15,
    },
    {
      field: 'total_paid',
      header: 'Received',
      type: 'amount',
      sortable: true,
      exportable: true,
      align: 'right',
      exportWidth: 15,
    },
    {
      field: 'balance',
      header: 'Balance',
      type: 'amount',
      sortable: true,
      exportable: true,
      align: 'right',
      exportWidth: 15,
    },
    {
      field: 'actions',
      header: 'Actions',
      type: 'actions',
      sortable: false,
      exportable: false,
      align: 'right',
    },
  ];

  // DataTable actions configuration
  const actions = [
    {
      type: 'view',
      label: 'View',
      icon: <Visibility sx={{ mr: 1 }} />,
      condition: () => hasPermission('invoices', 'view'),
    },
    {
      type: 'edit',
      label: 'Edit',
      icon: <Edit sx={{ mr: 1 }} />,
      condition: () => hasPermission('invoices', 'edit'),
    },
    {
      type: 'payment_history',
      label: 'Payment History',
      icon: <History sx={{ mr: 1 }} />,
      condition: () => hasPermission('invoices', 'view'),
    },
    {
      type: 'receive_payment',
      label: 'Receive Payment',
      icon: <Payment sx={{ mr: 1 }} />,
      // Only show for invoices with balance + users who can edit invoices
      condition: (invoice) => hasPermission('invoices', 'edit') && invoice.balance > 0,
    },
    {
      type: 'delete',
      label: 'Delete',
      icon: <Delete sx={{ mr: 1 }} />,
      color: 'error',
      condition: () => hasPermission('invoices', 'delete'),
    },
  ];

  useEffect(() => {
    fetchData();
    fetchPermissions();
  }, []);

  useEffect(() => {
    if (dateFilter !== 'custom') {
      setCustomDateRange({ startDate: '', endDate: '' });
    }
  }, [dateFilter]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [invoicesRes, clientsRes] = await Promise.all([
        apiService.get('/invoices'),
        apiService.get('/clients'),
      ]);

      if (invoicesRes.success) {
        const invoicesWithBalance = (invoicesRes.data.invoices || []).map(invoice => ({
          ...invoice,
          // No draft flow anymore — keep UI consistent even if old records exist
          status: 'submitted',
          balance: (invoice.total_amount || 0) - (invoice.total_paid || 0)
        }));
        setInvoices(invoicesWithBalance);
      }
      if (clientsRes.success) setClients(clientsRes.data.clients || []);
    } catch (err) {
      setError('Error fetching data: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (event) => {
    setSearchTerm(event.target.value);
  };

  const dateFilterTabs = [
    { value: 'today', label: 'Today' },
    { value: 'this_week', label: 'This Week' },
    { value: 'current_month', label: 'This Month' },
    { value: 'this_year', label: 'This Year' },
    { value: 'custom', label: 'Custom' },
  ];

  const parseInvoiceDate = (dateValue) => {
    if (!dateValue) return new Date(NaN);
    if (typeof dateValue === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(dateValue)) {
      // Force local date parsing for YYYY-MM-DD
      return new Date(`${dateValue}T00:00:00`);
    }
    return new Date(dateValue);
  };

  const getFilteredInvoices = () => {
    const q = String(searchTerm || '').trim().toLowerCase();
    const qDigits = q.replace(/\D/g, '');

    let filtered = (invoices || []).filter((invoice) => {
      // If no query, keep all (date filter will apply below)
      if (!q) return true;

      const client = (clients || []).find((c) => Number(c.id) === Number(invoice.client_id));
      const clientName = client ? `${client.first_name || ''} ${client.last_name || ''}`.trim() : '';
      const clientMobile = client?.mobile ? String(client.mobile) : '';

      const invoiceNoRaw =
        invoice?.invoice_number ?? invoice?.invoice_no ?? invoice?.number ?? '';
      const invoiceNoStr = String(invoiceNoRaw).toLowerCase();
      const invoiceNoDigits = String(invoiceNoRaw).replace(/\D/g, '');

      const invoiceId = invoice?.id != null ? String(invoice.id).toLowerCase() : '';

      const matchesClientName = clientName.toLowerCase().includes(q);
      const matchesClientMobile = qDigits ? clientMobile.includes(qDigits) : clientMobile.toLowerCase().includes(q);
      const matchesInvoiceNumber =
        invoiceNoStr.includes(q) || (qDigits && invoiceNoDigits.includes(qDigits));
      const matchesInvoiceId = invoiceId && (invoiceId === q || invoiceId.includes(q));

      return matchesClientName || matchesClientMobile || matchesInvoiceNumber || matchesInvoiceId;
    });

    // Filter by date range
    filtered = filtered.filter(invoice => {
      const invoiceDate = parseInvoiceDate(invoice.invoice_date);
      const today = new Date();
      
      switch (dateFilter) {
        case 'today': {
          return (
            invoiceDate.getDate() === today.getDate() &&
            invoiceDate.getMonth() === today.getMonth() &&
            invoiceDate.getFullYear() === today.getFullYear()
          );
        }
        case 'this_week': {
          // Week starts on Monday (common business convention)
          const day = today.getDay(); // 0=Sun, 1=Mon, ...
          const diffToMonday = day === 0 ? 6 : day - 1;
          const startOfWeek = new Date(today);
          startOfWeek.setHours(0, 0, 0, 0);
          startOfWeek.setDate(today.getDate() - diffToMonday);

          const endOfWeek = new Date(startOfWeek);
          endOfWeek.setDate(startOfWeek.getDate() + 6);
          endOfWeek.setHours(23, 59, 59, 999);

          return invoiceDate >= startOfWeek && invoiceDate <= endOfWeek;
        }
        case 'current_month':
          return invoiceDate.getMonth() === today.getMonth() && 
                 invoiceDate.getFullYear() === today.getFullYear();
        case 'last_month':
          const lastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
          return invoiceDate.getMonth() === lastMonth.getMonth() && 
                 invoiceDate.getFullYear() === lastMonth.getFullYear();
        case 'last_3_months':
          const threeMonthsAgo = new Date(today.getFullYear(), today.getMonth() - 3, 1);
          return invoiceDate >= threeMonthsAgo;
        case 'this_year':
          return invoiceDate.getFullYear() === today.getFullYear();
        case 'custom':
          if (customDateRange.startDate && customDateRange.endDate) {
            const startDate = new Date(customDateRange.startDate + 'T00:00:00');
            const endDate = new Date(customDateRange.endDate + 'T23:59:59');
            
            // Validate date range (max 1 year)
            const oneYearFromStart = new Date(startDate);
            oneYearFromStart.setFullYear(oneYearFromStart.getFullYear() + 1);
            
            if (endDate > oneYearFromStart) {
              return false; // Skip invalid date ranges
            }
            
            return invoiceDate >= startDate && invoiceDate <= endDate;
          }
          return true;
        default:
          return true;
      }
    });

    return filtered.sort((a, b) => new Date(b.invoice_date) - new Date(a.invoice_date));
  };





  const handleCreateInvoice = () => {
    if (!hasPermission('invoices', 'add')) {
      setError('Permission denied');
      return;
    }
    navigate('/dashboard/invoices/new');
  };

  const handleViewInvoice = (invoice) => {
    navigate(`/dashboard/invoices/view/${invoice.id}`);
  };

  const handleEditInvoice = (invoice) => {
    if (!hasPermission('invoices', 'edit')) {
      setError('Permission denied');
      return;
    }
    navigate(`/dashboard/invoices/edit/${invoice.id}`);
  };





  const handleDeleteInvoice = async () => {
    if (!hasPermission('invoices', 'delete')) {
      setError('Permission denied');
      return;
    }
    if (selectedInvoice) {
      try {
        const response = await apiService.delete(`/invoices/${selectedInvoice.id}`);
        if (response.success) {
          fetchData();
        } else {
          setError('Failed to delete invoice');
        }
      } catch (err) {
        setError('Error deleting invoice: ' + err.message);
      }
    }
  };

  const handleStatusChange = async (invoiceId, newStatus) => {
    try {
      const response = await apiService.put(`/invoices/${invoiceId}`, { status: newStatus });
      if (response.success) {
        fetchData();
      } else {
        setError('Error updating status');
      }
    } catch (err) {
      setError('Error updating status: ' + err.message);
    }
  };

  // DataTable action handler
  const handleTableAction = (actionType, invoice) => {
    switch (actionType) {
      case 'view':
        handleViewInvoice(invoice);
        break;
      case 'edit':
        if (!hasPermission('invoices', 'edit')) {
          setError('Permission denied');
          return;
        }
        handleEditInvoice(invoice);
        break;
      case 'payment_history':
        if (!hasPermission('invoices', 'view')) {
          setError('Permission denied');
          return;
        }
        handlePaymentHistory(invoice);
        break;
      case 'receive_payment':
        if (!hasPermission('invoices', 'edit')) {
          setError('Permission denied');
          return;
        }
        handleReceiveBalance(invoice, invoice.balance);
        break;
      case 'delete':
        if (!hasPermission('invoices', 'delete')) {
          setError('Permission denied');
          return;
        }
        setSelectedInvoice(invoice);
        setDeleteConfirmDialog(true);
        break;
      default:
        console.log('Unknown action:', actionType);
    }
  };

  const handlePaymentHistory = async (invoice) => {
    try {
      setLoadingPayments(true);
      setSelectedInvoice(invoice);
      setPaymentHistoryDialog(true);
      
      const response = await apiService.get(`/invoices/${invoice.id}`);
      if (response.success && response.data.invoice.payments) {
        setPaymentHistory(response.data.invoice.payments);
      } else {
        setPaymentHistory([]);
      }
    } catch (err) {
      setError('Error fetching payment history: ' + err.message);
      setPaymentHistory([]);
    } finally {
      setLoadingPayments(false);
    }
  };

  const handlePrintInvoice = async (invoice) => {
    try {
      // Fetch full invoice details if not already loaded
      const response = await apiService.get(`/invoices/${invoice.id}`);
      if (response.success) {
        const fullInvoice = response.data.invoice;
        const client = getClient(fullInvoice.client_id);
        
        // Create invoice element
        const invoiceElement = (
          <InvoicePrint 
            invoice={fullInvoice} 
            client={client} 
            isPrintMode={true}
          />
        );
        
        // Print the invoice
        printInvoice(invoiceElement);
      } else {
        setError('Failed to fetch invoice details');
      }
    } catch (err) {
      setError('Error printing invoice: ' + err.message);
    }
  };


  const handleReceiveBalance = (invoice, balanceAmount) => {
    setReceiveBalanceData({
      payment_mode: 'cash',
      payment_date: new Date().toISOString().split('T')[0],
      reference_number: '',
      notes: `Balance payment for invoice #${invoice.invoice_number}`,
      amount: balanceAmount
    });
    setSelectedInvoice({ ...invoice, balanceAmount });
    setReceiveBalanceDialog(true);
  };

  const handleSavePayment = async () => {
    try {
      setSavingPayment(true);
      setError('');

      // Validate amount
      const balanceAmount = selectedInvoice.balanceAmount || 0;
      if (receiveBalanceData.amount <= 0) {
        setError('Payment amount must be greater than zero');
        return;
      }
      if (receiveBalanceData.amount > balanceAmount) {
        setError(`Payment amount cannot exceed the balance amount of ${formatPrice(balanceAmount)}`);
        return;
      }

      const paymentData = {
        invoice_id: selectedInvoice.id,
        payment_date: receiveBalanceData.payment_date,
        payment_mode: receiveBalanceData.payment_mode,
        amount: receiveBalanceData.amount,
        reference_number: receiveBalanceData.reference_number || null,
        notes: receiveBalanceData.notes || null
      };

      const response = await apiService.post('/payments', paymentData);
      if (response.success) {
        setReceiveBalanceDialog(false);
        setReceiveBalanceData({
          payment_mode: 'cash',
          payment_date: new Date().toISOString().split('T')[0],
          reference_number: '',
          notes: '',
          amount: 0
        });
        fetchData(); // Refresh the invoices list
      } else {
        setError('Failed to save payment');
      }
    } catch (err) {
      setError('Error saving payment: ' + err.message);
    } finally {
      setSavingPayment(false);
    }
  };

  const getClient = (clientId) => {
    return clients.find(c => c.id === clientId);
  };

  const getInitials = (firstName, lastName) => {
    return `${firstName?.charAt(0) || ''}${lastName?.charAt(0) || ''}`.toUpperCase();
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString();
  };

  const getStatusColor = (status) => {
    const statusOption = statusOptions.find(opt => opt.value === status);
    return statusOption ? statusOption.color : 'default';
  };

  const filteredInvoices = getFilteredInvoices();

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <Typography>Loading invoices...</Typography>
      </Box>
    );
  }

  return (
    <FeatureGuard feature="Invoice Management">
      <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" fontWeight="bold">
          Invoices
        </Typography>
        <Box display="flex" gap={2}>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={fetchData}
          >
            Refresh
          </Button>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={handleCreateInvoice}
            disabled={!hasPermission('invoices', 'add')}
          >
            New
          </Button>
        </Box>
      </Box>



      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      {/* Search and Date Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {/* Date range tabs (matches dashboard style) */}
            <Box sx={{ display: 'flex', justifyContent: 'center' }}>
              <ToggleButtonGroup
                value={dateFilter}
                exclusive
                onChange={(_, next) => {
                  if (next) setDateFilter(next);
                }}
                sx={{
                  borderRadius: '999px',
                  border: 1,
                  borderColor: 'primary.main',
                  overflow: 'hidden',
                  flexWrap: 'wrap',
                  '& .MuiToggleButtonGroup-grouped': {
                    border: 'none',
                    borderRadius: 0,
                  },
                  '& .MuiToggleButton-root': {
                    textTransform: 'none',
                    fontWeight: 500,
                    px: 3,
                    py: 1,
                    color: 'primary.main',
                    '&.Mui-selected': {
                      bgcolor: 'primary.main',
                      color: 'common.white',
                      '&:hover': {
                        bgcolor: 'primary.dark',
                      },
                    },
                  },
                }}
              >
                {dateFilterTabs.map((tab) => (
                  <ToggleButton key={tab.value} value={tab.value}>
                    {tab.label}
                  </ToggleButton>
                ))}
              </ToggleButtonGroup>
            </Box>

            {/* Custom range picker (above search bar) */}
            {dateFilter === 'custom' && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, width: '100%' }}>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start', flexWrap: 'wrap' }}>
                  <TextField
                    type="date"
                    label="Start Date"
                    value={customDateRange.startDate}
                    onChange={(e) => setCustomDateRange(prev => ({ ...prev, startDate: e.target.value }))}
                    size="small"
                    inputProps={{
                      max: new Date().toISOString().split('T')[0]
                    }}
                    InputLabelProps={{ shrink: true }}
                    helperText="Select start date"
                  />
                  <TextField
                    type="date"
                    label="End Date"
                    value={customDateRange.endDate}
                    onChange={(e) => setCustomDateRange(prev => ({ ...prev, endDate: e.target.value }))}
                    size="small"
                    inputProps={{
                      min: customDateRange.startDate || undefined,
                      max: new Date().toISOString().split('T')[0]
                    }}
                    InputLabelProps={{ shrink: true }}
                    disabled={!customDateRange.startDate}
                    helperText={!customDateRange.startDate ? "Select start date first" : "Select end date"}
                    error={customDateRange.startDate && customDateRange.endDate &&
                           new Date(customDateRange.startDate) > new Date(customDateRange.endDate)}
                  />
                  {customDateRange.startDate && customDateRange.endDate && (
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => setCustomDateRange({ startDate: '', endDate: '' })}
                      sx={{ alignSelf: 'flex-end' }}
                    >
                      Clear Dates
                    </Button>
                  )}
                </Box>

                {/* Error and warning messages displayed below */}
                {customDateRange.startDate && customDateRange.endDate &&
                 new Date(customDateRange.startDate) > new Date(customDateRange.endDate) && (
                  <Typography variant="caption" color="error" sx={{ display: 'block' }}>
                    End date must be after start date
                  </Typography>
                )}
                {customDateRange.startDate && customDateRange.endDate &&
                 new Date(customDateRange.endDate) > new Date(new Date(customDateRange.startDate).setFullYear(new Date(customDateRange.startDate).getFullYear() + 1)) && (
                  <Typography variant="caption" color="error" sx={{ display: 'block' }}>
                    Date range should not exceed 1 year for better performance
                  </Typography>
                )}
              </Box>
            )}

            {/* Search */}
            <TextField
              placeholder="Search invoices by client name or invoice number..."
              value={searchTerm}
              onChange={handleSearch}
              sx={{ width: '100%' }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
            />
          </Box>
          

        </CardContent>
      </Card>

            {/* Results Summary */}
      <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          Showing {filteredInvoices.length} invoices
          {dateFilter !== 'all' && (
            <span>
              {' '}for{' '}
              {dateFilter === 'today' && 'Today'}
              {dateFilter === 'this_week' && 'This Week'}
              {dateFilter === 'current_month' && 'This Month'}
              {dateFilter === 'last_month' && 'Last Month'}
              {dateFilter === 'last_3_months' && 'Last 3 Months'}
              {dateFilter === 'this_year' && 'This Year'}
              {dateFilter === 'custom' && customDateRange.startDate && customDateRange.endDate && 
                `${new Date(customDateRange.startDate).toLocaleDateString()} - ${new Date(customDateRange.endDate).toLocaleDateString()}`
              }
            </span>
          )}
        </Typography>

      </Box>

      {/* Invoices Table */}
      <DataTable
        data={filteredInvoices}
        columns={columns}
        actions={actions}
        onAction={handleTableAction}
        itemsPerPage={50}
        showPagination={true}
        showExport={hasPermission('invoices', 'export')}
        showPDFExport={false}
        exportFileName="invoices"
        getClient={getClient}
        getInitials={getInitials}
        formatPrice={formatPrice}
        formatDate={formatDate}
        statusOptions={statusOptions}
        getStatusColor={getStatusColor}
        emptyStateIcon={Receipt}
        emptyStateTitle="No invoices yet"
        emptyStateSubtitle="Create your first invoice to get started"
        searchEmptyStateTitle="No invoices found matching your search"
        searchEmptyStateSubtitle="Try adjusting your search terms"
        hasSearchResults={!!searchTerm}
      />



      {/* Payment History Dialog */}
      <Dialog 
        open={paymentHistoryDialog} 
        onClose={() => setPaymentHistoryDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Payment History - Invoice #{selectedInvoice?.invoice_number}
        </DialogTitle>
        <DialogContent>
          {loadingPayments ? (
            <Box display="flex" justifyContent="center" p={3}>
              <Typography>Loading payment history...</Typography>
            </Box>
          ) : paymentHistory.length === 0 ? (
            <Box textAlign="center" p={3}>
              <Typography color="text.secondary">
                No payment history found for this invoice.
              </Typography>
            </Box>
          ) : (
            <List>
              {paymentHistory.map((payment, index) => (
                <React.Fragment key={payment.id}>
                  <ListItem>
                    <ListItemText
                      primary={
                        <Box display="flex" justifyContent="space-between" alignItems="center">
                          <Typography variant="subtitle1" fontWeight="bold">
                            {formatPrice(payment.amount)}
                          </Typography>
                          <Chip 
                            label={payment.payment_mode} 
                            color="primary" 
                            size="small"
                          />
                        </Box>
                      }
                      secondary={
                        <Box>
                          <Typography variant="body2" color="text.secondary">
                            Date: {formatDate(payment.payment_date)}
                          </Typography>
                          {payment.reference_number && (
                            <Typography variant="body2" color="text.secondary">
                              Reference: {payment.reference_number}
                            </Typography>
                          )}
                          {payment.notes && (
                            <Typography variant="body2" color="text.secondary">
                              Notes: {payment.notes}
                            </Typography>
                          )}
                        </Box>
                      }
                    />
                  </ListItem>
                  {index < paymentHistory.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPaymentHistoryDialog(false)}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Receive Balance Dialog */}
      <Dialog 
        open={receiveBalanceDialog} 
        onClose={() => setReceiveBalanceDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Receive Balance Payment - Invoice #{selectedInvoice?.invoice_number}
        </DialogTitle>
                <DialogContent>
          <Box sx={{ mb: 3 }}>
            <Typography variant="h6" color="primary" gutterBottom>
              Balance Amount: {formatPrice(selectedInvoice?.balanceAmount || 0)}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Client: {selectedInvoice && getClient(selectedInvoice.client_id) ? 
                `${getClient(selectedInvoice.client_id).first_name} ${getClient(selectedInvoice.client_id).last_name}` : 
                'Unknown Client'}
            </Typography>
 
          </Box>
          
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Payment Date"
              type="date"
              value={receiveBalanceData.payment_date}
              onChange={(e) => setReceiveBalanceData(prev => ({ ...prev, payment_date: e.target.value }))}
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
            
            <TextField
              label="Amount Received"
              type="number"
              value={receiveBalanceData.amount}
              onChange={(e) => setReceiveBalanceData(prev => ({ ...prev, amount: parseFloat(e.target.value) || 0 }))}
              fullWidth
              InputProps={{
                startAdornment: <InputAdornment position="start">₹</InputAdornment>,
              }}
              helperText={`Maximum balance: ${formatPrice(selectedInvoice?.balanceAmount || 0)}`}
            />
            
            {receiveBalanceData.amount > 0 && (
              <Box sx={{ p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  Remaining Balance: <strong>{formatPrice((selectedInvoice?.balanceAmount || 0) - receiveBalanceData.amount)}</strong>
                </Typography>
              </Box>
            )}
            
            <TextField
              select
              label="Payment Mode"
              value={receiveBalanceData.payment_mode}
              onChange={(e) => setReceiveBalanceData(prev => ({ ...prev, payment_mode: e.target.value }))}
              fullWidth
            >
              <MenuItem value="cash">Cash</MenuItem>
              <MenuItem value="card">Card</MenuItem>
              <MenuItem value="upi">UPI</MenuItem>
              <MenuItem value="bank_transfer">Bank Transfer</MenuItem>
              <MenuItem value="cheque">Cheque</MenuItem>
              <MenuItem value="other">Other</MenuItem>
            </TextField>
            
            <TextField
              label="Reference Number"
              value={receiveBalanceData.reference_number}
              onChange={(e) => setReceiveBalanceData(prev => ({ ...prev, reference_number: e.target.value }))}
              fullWidth
              placeholder="Transaction ID, Cheque number, etc."
            />
            
            <TextField
              label="Notes"
              value={receiveBalanceData.notes}
              onChange={(e) => setReceiveBalanceData(prev => ({ ...prev, notes: e.target.value }))}
              fullWidth
              multiline
              rows={3}
              placeholder="Additional notes about this payment"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReceiveBalanceDialog(false)}>
            Cancel
          </Button>
          <Button 
            onClick={handleSavePayment}
            variant="contained"
            disabled={savingPayment}
            startIcon={<AccountBalance />}
          >
            {savingPayment ? 'Saving...' : 'Receive Payment'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog 
        open={deleteConfirmDialog} 
        onClose={() => setDeleteConfirmDialog(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Delete Invoice
        </DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete invoice #{selectedInvoice?.invoice_number}?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            This action cannot be undone. All associated data will be permanently removed.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmDialog(false)}>
            Cancel
          </Button>
          <Button 
            onClick={() => {
              handleDeleteInvoice();
              setDeleteConfirmDialog(false);
            }}
            variant="contained"
            color="error"
            startIcon={<Delete />}
          >
            Delete Invoice
          </Button>
        </DialogActions>
      </Dialog>

      </Box>
    </FeatureGuard>
  );
};

export default Invoices;
